import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { AssignRolesDto, UserDetail } from '@/types/settings';

interface UpdateUserRolesVariables {
  userId: string;
  currentRoleIds: string[];
  nextRoleIds: string[];
}

export function useUpdateUserRoles() {
  const queryClient = useQueryClient();

  return useApiMutation<UserDetail, UpdateUserRolesVariables>({
    mutationFn: async ({ userId, currentRoleIds, nextRoleIds }) => {
      const current = new Set(currentRoleIds);
      const next = new Set(nextRoleIds);
      const roleIdsToAdd = [...next].filter((roleId) => !current.has(roleId));
      const roleIdsToRemove = [...current].filter((roleId) => !next.has(roleId));

      let updatedUser: UserDetail | null = null;

      if (roleIdsToAdd.length > 0) {
        updatedUser = await apiPost<UserDetail, AssignRolesDto>(
          operationsClient,
          settingsEndpoints.assignUserRoles(userId),
          { roleIds: roleIdsToAdd },
        );
      }

      for (const roleId of roleIdsToRemove) {
        updatedUser = await apiDelete<UserDetail>(
          operationsClient,
          settingsEndpoints.removeUserRole(userId, roleId),
        );
      }

      return (
        updatedUser ?? apiGet<UserDetail>(operationsClient, settingsEndpoints.userDetail(userId))
      );
    },
    onSuccess: (_data, { userId }) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.users() });
      // Refresh the open user-detail card, which reads userDetail(userId).
      void queryClient.invalidateQueries({ queryKey: settingsKeys.userDetail(userId) });
      toast.success('Roles updated');
    },
  });
}
